/**
 * Connectivity heartbeat — resident-app wiring of the shared ConnectivityMonitor.
 *
 * Every 60s it probes the five connectivity layers (network / internet / api /
 * sip / mqtt), buffers the sample locally, and flushes the buffer to the API on
 * a state change or every 15 min (whichever comes first). The buffer is the
 * device's offline memory: if internet is down — exactly when this data matters
 * — samples are retained and delivered once the device recovers.
 */
import { ConnectivityMonitor } from '@caro/shared/telemetry';
import type {
  ConnectivitySample,
  ConnectivityHeartbeat,
  ConnectivityStorage,
} from '@caro/shared/telemetry';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { api } from '../hooks/useAuth';
import { isSipRegistered } from '../hooks/useSip';
import { getConnectionState as getMqttState } from './MqttTelemetry';

const BUFFER_KEY = 'caro_connectivity_buffer';
const apiBaseUrl = (import.meta as any).env?.VITE_API_URL || 'https://api.caro.care';

/**
 * The "temp file": a localStorage-backed buffer. It persists across WebView
 * restarts and is more than enough for ~240 small samples. Swap for
 * @capacitor/filesystem if a true on-disk file is ever required — the
 * ConnectivityStorage interface is the only thing that has to change.
 */
const storage: ConnectivityStorage = {
  read: async () => {
    try {
      return localStorage.getItem(BUFFER_KEY);
    } catch {
      return null;
    }
  },
  write: async (data: string) => {
    try {
      localStorage.setItem(BUFFER_KEY, data);
    } catch {
      // quota exceeded / storage unavailable — drop silently, next flush retries
    }
  },
};

/** One probe of all five layers. internet+api use lightweight external fetches. */
async function probe(): Promise<Omit<ConnectivitySample, 'at'>> {
  const network = typeof navigator !== 'undefined' ? navigator.onLine : true;

  // Internet reachability — a public endpoint independent of our API.
  let internet = false;
  try {
    const res = await fetch('https://dns.google/resolve?name=api.caro.care&type=A', {
      signal: AbortSignal.timeout(5000),
    });
    internet = res.ok;
  } catch {
    internet = false;
  }

  // CARO API reachability + round-trip latency.
  let apiOk = false;
  let apiLatencyMs: number | undefined;
  try {
    const t0 = Date.now();
    const res = await fetch(`${apiBaseUrl}${API_ENDPOINTS.HEALTH}`, {
      signal: AbortSignal.timeout(5000),
    });
    apiOk = res.ok;
    apiLatencyMs = Date.now() - t0;
    if (apiOk) internet = true; // reaching our API proves internet works
  } catch {
    apiOk = false;
  }

  return {
    network,
    internet,
    api: apiOk,
    sip: isSipRegistered(),
    mqtt: getMqttState() === 'connected',
    ...(apiLatencyMs !== undefined ? { apiLatencyMs } : {}),
  };
}

async function flush(heartbeat: ConnectivityHeartbeat): Promise<void> {
  // Must reject on failure so the monitor retains the buffer and retries.
  await api.post(API_ENDPOINTS.DEVICE_CONNECTIVITY, heartbeat);
}

let monitor: ConnectivityMonitor | null = null;

/** Start the heartbeat (idempotent). Call when the device is authenticated. */
export function startConnectivityHeartbeat(): void {
  if (monitor) return;
  monitor = new ConnectivityMonitor(
    { probe, storage, flush },
    { checkIntervalMs: 60_000, keepaliveMs: 900_000 },
  );
  void monitor.start();
}

/** Stop the heartbeat (e.g. on logout). */
export function stopConnectivityHeartbeat(): void {
  monitor?.stop();
  monitor = null;
}
